import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTRPC } from '../../lib/trpc';
import { Sheet } from '../ui/Sheet';
import { Notice } from '../ui/Badge';
import { Skeleton } from '../ui/Skeleton';
import { dateOnly } from '../../lib/format';

export interface Rig {
  userId: string;
  name: string;
  vehicle: { assetId: string; name: string; plate: string | null } | null;
}

/**
 * The rig drill-down (114 part 6, design 113/A1b).
 *
 * > *"When you look at a schedule, you're going to see the tech and all the equipment that
 * > is with that tech, including the van and everything tied to it. That doesn't have to
 * > show on one screen. It can be like a drill down."*
 *
 * ⭐ **A drill-down, explicitly not a board column.** The lane header names the tech and
 * their van in one line; everything else about the rig lives here, one click away. Putting
 * the kit on the board would cost every lane the vertical space the ADDRESS needs, and the
 * address is what decides whether a stop is on the way.
 *
 * ⚠️ **Two lists would be wrong here and there is only one.** A tool has two holders —
 * a person (`asset_assignments`) and a van (`asset_placements`) — and nothing reconciles
 * them. This panel asks *what is on this van*, so it reads PLACEMENTS. Who is responsible
 * for each tool is a different question with a different answer, and it lives on the asset's
 * own page rather than being merged into this list under a heading that would then be true
 * of neither.
 *
 * ⚠️ **`liveOn` is the VIEWED day, never today.** A board showing Thursday must show
 * Thursday's kit, for the same reason its lanes use Thursday's van assignment.
 */
export function RigPanel({
  rig,
  dayIso,
  onClose,
}: {
  rig: Rig | null;
  /** The day the board is showing, as `YYYY-MM-DD` on the shop's clock. */
  dayIso: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const vehicleAssetId = rig?.vehicle?.assetId;
  const kit = useQuery({
    ...trpc.asset.placements.queryOptions({
      vehicleAssetId: vehicleAssetId ?? '',
      liveOn: dayIso,
    }),
    // No van, no kit to ask for — and asking with an empty id would 400 on every open of a
    // vanless tech's lane, which is a state the board is designed to have.
    enabled: Boolean(vehicleAssetId),
  });

  return (
    <Sheet
      open={rig !== null}
      onClose={onClose}
      title={rig?.name ?? ''}
      badge={
        rig ? (
          <span className="text-xs text-muted-foreground">
            {rig.vehicle
              ? [rig.vehicle.name, rig.vehicle.plate].filter(Boolean).join(' · ')
              : 'No van assigned'}
          </span>
        ) : null
      }
    >
      {rig ? (
        <div className="space-y-4 text-sm">
          {!rig.vehicle ? (
            /* Not an error and not an empty list — a fact the office has to see. A tech
               with work and no van is exactly the thing the tech-keyed lane exists to keep
               on screen rather than dropping. */
            <Notice tone="warn">
              No van is assigned to {rig.name} on {dateOnly(dayIso)}, so there is no kit
              to show. Assign one on the vehicle&apos;s page and it appears here.
            </Notice>
          ) : (
            <>
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-muted-foreground">Van</span>
                <Link
                  to="/fleet/assets/$assetId"
                  params={{ assetId: rig.vehicle.assetId }}
                  className="text-right font-medium text-[var(--sw-brand-text)] hover:underline"
                >
                  {rig.vehicle.name}
                </Link>
              </div>
              {rig.vehicle.plate ? (
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-muted-foreground">Plate</span>
                  <span className="text-right font-medium tabular-nums">
                    {rig.vehicle.plate}
                  </span>
                </div>
              ) : null}

              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  On board {dateOnly(dayIso)}
                </p>
                {kit.isError ? (
                  /* Named as a failure, never rendered as an empty van. "No equipment" and
                     "we could not ask" are different sentences and only one of them is
                     something the office should act on. */
                  <Notice tone="danger">
                    Couldn&apos;t load this van&apos;s kit — {kit.error.message}
                  </Notice>
                ) : !kit.data ? (
                  <Skeleton className="mt-2 h-16 w-full" />
                ) : kit.data.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Nothing is recorded on this van yet. Put a tool on it from the
                    tool&apos;s own page.
                  </p>
                ) : (
                  <ul className="mt-2 divide-y divide-border">
                    {kit.data.map((p) => (
                      <li key={p.id} className="flex items-baseline gap-3 py-2">
                        <Link
                          to="/fleet/assets/$assetId"
                          params={{ assetId: p.assetId }}
                          className="min-w-0 flex-1 truncate font-medium hover:underline"
                        >
                          {p.assetName}
                        </Link>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          since {dateOnly(p.startsOn)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {/* ⚠️ Says which question this list answers. Without it a reader assumes
                    these tools are this tech's responsibility, and they are not — placement
                    and assignment are separate records that a shop deliberately lets
                    disagree. */}
                <p className="mt-3 text-xs text-muted-foreground">
                  This is what the van carries. Who is responsible for each tool is a
                  separate record, on the tool&apos;s own page.
                </p>
              </div>
            </>
          )}
        </div>
      ) : null}
    </Sheet>
  );
}
